import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { FiInfo } from 'react-icons/fi';
import AIInsightsPlugin from './Plugins/AIInsightsPlugin';
import { PALE_WHITE_1, PALE_WHITE_4, PALE_WHITE_6 } from '../../appearance/Colors';

const formatInfoDate = value => {
    const date = value ? new Date(value) : null;
    return date && !isNaN(date.getTime()) ? format(date, 'dd MMM yyyy, HH:mm') : null;
};

function MessageInfoSection({ message, reminderDetails }) {
    const { t: lnT } = useTranslation();
    const [expanded, setExpanded] = React.useState(false);
    const sectionId = React.useId();
    const wrapperRef = React.useRef(null);
    const buttonRef = React.useRef(null);
    React.useEffect(() => {
        if (!expanded) return;
        const dismiss = event => {
            if (!wrapperRef.current?.contains(event.target)) setExpanded(false);
        };
        const onKeyDown = event => {
            if (event.key === 'Escape') {
                setExpanded(false);
                buttonRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', dismiss);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', dismiss);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [expanded]);
    React.useEffect(() => setExpanded(false), [message?.id]);
    const rows = [
        [lnT('messages:path'), message?.folder?.title],
        [lnT('folders:contributedBy'), message?.contributionSource === 'ANONYMOUS_LINK' ? lnT('folders:anonymousVisitor') : null],
        [lnT('messages:created_22d435a3'), formatInfoDate(message?.createdAt)],
        [lnT('messages:modified'), formatInfoDate(message?.updatedAt)]
    ].filter(([, value]) => value);

    return (
        <Wrapper ref={wrapperRef}>
            <InfoButton
                ref={buttonRef}
                aria-label={lnT('messages:info')}
                title={lnT('messages:info')}
                type="button"
                aria-expanded={expanded}
                aria-controls={sectionId}
                onClick={() => setExpanded(previous => !previous)}
            >
                <FiInfo size={20} aria-hidden="true" />
            </InfoButton>
            {expanded ? (
                <InfoContent id={sectionId}>
                    {rows.length ? <Metadata>
                        {rows.map(([label, value]) => <Row key={label}><dt>{label}</dt><dd>{value}</dd></Row>)}
                    </Metadata> : null}
                    {reminderDetails}
                    <AIInsightsPlugin message={message} frameless />
                </InfoContent>
            ) : null}
        </Wrapper>
    );
}

const Wrapper = styled.div`
    position: relative;
    z-index: 19;
    width: 100%;
    display: flex;
    justify-content: flex-end;
    pointer-events: none;
    margin-left: auto;
    margin-top: 12px;
    flex-shrink: 0;
`;
const InfoButton = styled.button`
    pointer-events: auto;
    border: 0;
    border-radius: 999px;
    width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: ${PALE_WHITE_4};
    cursor: pointer;
    transition: background-color 120ms ease;
    &:hover { background: ${PALE_WHITE_1}; }
    &:focus-visible {
        outline: 2px solid ${PALE_WHITE_6};
        outline-offset: 2px;
    }
`;
const InfoContent = styled.div`
    pointer-events: auto;
    position: absolute;
    bottom: calc(100% + 8px);
    right: 0;
    box-sizing: border-box;
    width: min(420px, 100%);
    max-height: min(360px, 55vh);
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    border: 1px solid ${PALE_WHITE_1};
    border-radius: 12px;
    background: rgba(50, 54, 57, 0.82);
    backdrop-filter: saturate(140%) blur(24px);
    -webkit-backdrop-filter: saturate(140%) blur(24px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
`;
const Metadata = styled.dl`
    margin: 0;
    padding: 0;
    color: ${PALE_WHITE_6};
    font-size: 12px;
`;
const Row = styled.div`
    display: flex;
    gap: 12px;
    margin: 4px 0;
    dt { flex: 0 0 80px; }
    dd { margin: 0; overflow-wrap: anywhere; }
`;

export default MessageInfoSection;
